import { AmbienceMixer, fadeParam } from './ambience';
import { InstrumentBank, PERCUSSION } from './instruments';
import { type BarPlan, ThemeSequencer } from './sequencer';
import { type AmbienceId, type Layer, type ThemeDef, type ThemeId, THEMES } from './themes';

export { THEMES, THEME_IDS, themeForZone, LEITMOTIF } from './themes';
export type { AmbienceId, InstrumentId, Layer, PartDef, ThemeDef, ThemeId } from './themes';
export { ThemeSequencer, validateTheme } from './sequencer';
export type { BarPlan, NoteEvent } from './sequencer';

/**
 * Generative music engine.
 *
 * - One `ThemePlayer` per playing theme: a seeded `ThemeSequencer` produces
 *   bars, which are scheduled a whole bar at a time as soon as the bar start
 *   enters the look-ahead window ("A Tale of Two Clocks": a coarse JS timer
 *   pumps, sample-accurate Web Audio timestamps play).
 * - `setTheme` crossfades (equal-power, ~3 s) to a fresh player; the old one
 *   stops scheduling at the end of its fade and is disposed once its notes
 *   have rung out.
 * - `setCombat` fades a percussion/ostinato layer in and ducks the base layer
 *   slightly; combat-only parts are not even scheduled while it is off.
 * - Ambience beds follow the theme unless overridden with `setAmbience`.
 */

export interface MusicHost {
  ctx: BaseAudioContext;
  music: { dry: AudioNode; wet: AudioNode };
  ambient: { dry: AudioNode; wet: AudioNode };
}

/** Base layer level while the combat layer plays (≈ -1 dB). */
const BASE_DUCK = 0.9;
/** How long a faded-out player keeps its nodes for ringing tails. */
const TAIL = 6;

class ThemePlayer {
  readonly out: GainNode;
  private readonly wetSend: GainNode;
  private readonly level: GainNode;
  private readonly layerGain: Record<Layer, GainNode>;
  private readonly bank: InstrumentBank;
  readonly seq: ThemeSequencer;
  private readonly spb: number;
  private nextBarTime: number;
  private combatOn: boolean;
  private combatUntil = 0;
  stopAt: number | null = null;
  scheduledNotes = 0;

  constructor(
    host: MusicHost,
    readonly theme: ThemeDef,
    seed: number,
    start: number,
    combat: boolean,
  ) {
    const ctx = host.ctx;
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(host.music.dry);
    this.wetSend = ctx.createGain();
    this.wetSend.gain.value = theme.reverb ?? 0.35;
    this.out.connect(this.wetSend);
    this.wetSend.connect(host.music.wet);
    this.level = ctx.createGain();
    this.level.gain.value = theme.level ?? 0.85;
    this.level.connect(this.out);
    const base = ctx.createGain();
    base.gain.value = combat ? BASE_DUCK : 1;
    base.connect(this.level);
    const fight = ctx.createGain();
    fight.gain.value = combat ? 1 : 0;
    fight.connect(this.level);
    this.layerGain = { base, combat: fight };
    this.bank = new InstrumentBank(ctx, { base, combat: fight }, { padCutoff: theme.padCutoff });
    this.seq = new ThemeSequencer(theme, seed);
    this.spb = 60 / theme.bpm;
    this.nextBarTime = start;
    this.combatOn = combat;
  }

  fadeIn(now: number, dur: number): void {
    fadeParam(this.out.gain, now, 1, dur);
  }

  fadeOut(now: number, dur: number): void {
    fadeParam(this.out.gain, now, 0, dur);
    this.stopAt = now + dur;
  }

  setCombat(on: boolean, now: number): void {
    if (on === this.combatOn) return;
    this.combatOn = on;
    const dur = on ? 1.5 : 2.5;
    if (!on) this.combatUntil = now + dur;
    fadeParam(this.layerGain.combat.gain, now, on ? 1 : 0, dur);
    fadeParam(this.layerGain.base.gain, now, on ? BASE_DUCK : 1, dur);
  }

  pump(now: number, horizon: number): void {
    // A throttled timer (background tab) must not dump a backlog of stale bars.
    if (this.nextBarTime < now - 0.1) this.nextBarTime = now + 0.05;
    while (this.nextBarTime < horizon) {
      if (this.stopAt !== null && this.nextBarTime >= this.stopAt) return;
      const plan = this.seq.nextBar();
      this.schedule(plan, this.nextBarTime);
      this.nextBarTime += plan.beats * this.spb;
    }
  }

  private schedule(plan: BarPlan, t0: number): void {
    const combat = this.combatOn || t0 < this.combatUntil;
    for (const ev of plan.events) {
      if (ev.layer === 'combat' && !combat) continue;
      // Light humanisation: drums tight, sustained parts looser.
      const jitter = (Math.random() - 0.5) * (PERCUSSION.has(ev.inst) ? 0.006 : 0.016);
      const t = Math.max(0, t0 + ev.beat * this.spb + (ev.beat > 0 ? jitter : Math.abs(jitter)));
      this.bank.play(
        ev.inst,
        ev.layer,
        t,
        ev.dur * this.spb,
        ev.midi,
        ev.vel * (0.94 + Math.random() * 0.12),
      );
      this.scheduledNotes++;
    }
  }

  finished(now: number): boolean {
    return this.stopAt !== null && now > this.stopAt + TAIL;
  }

  dispose(): void {
    this.bank.dispose();
    this.out.disconnect();
    this.wetSend.disconnect();
  }
}

export class MusicEngine {
  /** Seconds of music scheduled ahead of the clock while visible / hidden. */
  lookahead = 0.3;
  hiddenLookahead = 1.5;
  readonly ambience: AmbienceMixer;
  private players: ThemePlayer[] = [];
  private current: ThemePlayer | null = null;
  private currentId: ThemeId | null = null;
  private combat = false;
  private ambienceOverride: AmbienceId | null = null;
  private seedCounter: number;

  constructor(
    private readonly host: MusicHost,
    seed: number = Date.now() >>> 0,
  ) {
    this.seedCounter = seed;
    this.ambience = new AmbienceMixer(host.ctx, host.ambient);
  }

  get theme(): ThemeId | null {
    return this.currentId;
  }

  get inCombat(): boolean {
    return this.combat;
  }

  /** Number of theme players alive (current + fading). */
  get playerCount(): number {
    return this.players.length;
  }

  /** Notes scheduled by the current theme so far (diagnostics). */
  get scheduledNotes(): number {
    return this.current?.scheduledNotes ?? 0;
  }

  /** Crossfades to `id` (null = silence). Re-selecting the playing theme is a no-op. */
  setTheme(id: ThemeId | null, fade = 3): void {
    if (id === this.currentId) return;
    const now = this.host.ctx.currentTime;
    this.current?.fadeOut(now, fade);
    this.current = null;
    this.currentId = id;
    if (id) {
      const p = new ThemePlayer(this.host, THEMES[id], this.seedCounter++, now + 0.08, this.combat);
      p.fadeIn(now + 0.02, Math.max(0.4, fade * 0.8));
      this.players.push(p);
      this.current = p;
      p.pump(now, now + this.lookahead);
    }
    if (this.ambienceOverride === null) this.ambience.set(this.themeAmbience(), now);
  }

  setCombat(on: boolean): void {
    if (on === this.combat) return;
    this.combat = on;
    this.current?.setCombat(on, this.host.ctx.currentTime);
  }

  /** Forces an ambience bed; `null` follows the current theme again. */
  setAmbience(id: AmbienceId | null): void {
    this.ambienceOverride = id;
    this.ambience.set(id ?? this.themeAmbience(), this.host.ctx.currentTime);
  }

  private themeAmbience(): AmbienceId {
    return this.currentId ? (THEMES[this.currentId].ambience ?? 'none') : 'none';
  }

  /** Schedules everything due before `now + lookahead`; call every ~25–50 ms. */
  pump(now: number = this.host.ctx.currentTime, hidden = false): void {
    const horizon = now + (hidden ? this.hiddenLookahead : this.lookahead);
    for (const p of this.players) p.pump(now, horizon);
    const keep: ThemePlayer[] = [];
    for (const p of this.players) {
      if (p.finished(now)) p.dispose();
      else keep.push(p);
    }
    this.players = keep;
    this.ambience.pump(now, horizon);
  }

  dispose(): void {
    for (const p of this.players) p.dispose();
    this.players = [];
    this.current = null;
    this.currentId = null;
    this.ambience.dispose();
  }
}
